import { Injectable } from '@angular/core';
import { User } from '@isophi/core-legacy';
import { Tag } from '@isophi/pap-api';

@Injectable({
  providedIn: 'root',
})
export class RouterLinkFactory {
  /**
   * Navigate application to landing page.
   *
   * Page not required login.
   */
  public landingPage(): string[] {
    return ['/public'];
  }

  /**
   * Navigate application to homepage.
   *
   * Homepage is main page (component), which is rendered after login.
   * It is always safe to redirect to homepage.
   */
  public homepage(): string[] {
    return this.dashboard();
  }

  /**
   * Navigate application to login.
   */
  public login(): string[] {
    return ['/login'];
  }

  /**
   * External login confirmation
   */
  public externalLoginConfirmation(): string[] {
    return ['/login', 'external-confirmation'];
  }

  /**
   * Navigate application to result recommendation by child object.
   */
  public recommendation(papCategory: Tag): string[] {
    return ['/result', 'recommendation', papCategory?.uuid];
  }

  /**
   * Navigate application to result recommendation by child uuid.
   */
  public recommendationByChildUuid(childUuid: string): string[] {
    return ['/result', childUuid, 'recommendation'];
  }

  /**
   * Navigate application to result.
   */
  public result(): string[] {
    return ['/result'];
  }

  /**
   * Navigate application to forgotten password page, where user can reset his password.
   */
  public forgottenPassword(): string[] {
    return ['/login', 'forgotten-password'];
  }

  /**
   * Navigate application to registration page.
   */
  public registration(): string[] {
    return ['/registration'];
  }

  /**
   * Navigate application to change password page.
   *
   */
  public changePassword(): string[] {
    return ['/user', 'change-password'];
  }

  /**
   * Navigate application to licenses page.
   *
   */
  public licenses(): string[] {
    return ['/user', 'licenses'];
  }

  /**
   * Navigate application to sync.
   */
  public sync(): string[] {
    return ['/sync'];
  }

  /**
   * Navigate application to show page with successful activation of an account.
   */
  public activatedPage(): string[] {
    return ['/registration', 'activate'];
  }

  /**
   * Navigate application to show page with successful registration of an account.
   */
  public successPage(): string[] {
    return ['/registration', 'success'];
  }

  /**
   * Navigate application to fill questionnaire for a child.
   */
  public fillQuestionnaire(user?: User): string[] {
    if (user) {
      return ['/questionnaire', user.uuid, 'fill'];
    } else {
      return ['/questionnaire', 'fill'];
    }
  }

  /**
   * Navigate application to edit user data page.
   */
  public editUser(): string[] {
    return ['/user', 'edit-user'];
  }

  /**
   * Recommendations - no user
   */
  public recommendationNouser(): string[] {
    return ['/info', 'recommendation-nouser'];
  }

  /**
   * Info page - Why to use the tool.
   */
  public whyIsophi(): string[] {
    return ['/info', 'why-isophi'];
  }

  /**
   * Info page - contact info.
   */
  public contact(): string[] {
    return ['/info', 'contact'];
  }

  /**
   * Navigate application to dashboard.
   *
   */
  public dashboard(): string[] {
    return ['/info', 'dashboard'];
  }

  /**
   * Info page - What is self-evaluation about info
   */
  public selfEvaluationAbout(): string[] {
    return ['/info', 'self-evaluation-about'];
  }

  /**
   * Info page - Certificate of Participation
   */
  public participation(): string[] {
    return ['/info', 'participation-certificate'];
  }

  /**
   * Info page - Bonus: E-Learning
   */
  public bonusELearning(): string[] {
    return ['/info', 'bonus-e-learning'];
  }

  /**
   * Navigate application to portfolio page.
   */
  public portfolio(): string[] {
    return ['/portfolio'];
  }

  /**
   * Navigate application to portfolio page.
   */
  public plan(): string[] {
    return ['/plan'];
  }

  /**
   * Navigate application to create edit goal page.
   */
  public createEditTarget(id: string): string[] {
    return ['/plan', 'create-edit-target', id];
  }

  /**
   * Navigate application to create edit activity page.
   */
  public createEditActivity(id: string): string[] {
    return ['/portfolio', 'create-edit-activity', id];
  }

  /**
   * Navigate application to portfolio detail page.
   */
  public portfolioDetail(id: string): string[] {
    return ['/portfolio', id, 'detail'];
  }

  /**
   * Navigate application to plan detail page.
   */
  public planDetail(id: string): string[] {
    return ['/plan', id, 'detail'];
  }

  /**
   * Navigate application to recommendations page.
   */
  public recommendations(): string[] {
    return ['/recommendations'];
  }

  /**
   * Navigate application to recommendations detail page.
   */
  public recommendationsDetail(uuid: string): string[] {
    return ['/recommendations', uuid, 'detail'];
  }
}
